import Vue from "vue";
import {
    deleteLookedChatFromStorage,
    getConversationsMeta,
    getLookedChatIds,
    getMyChatIds,
    getPendingMessages,
    pushLookedChatId,
    setConversationsMeta,
    setMyChatIds,
    setPendingMessages,
    setWatchingChatIds,
} from "../../utils/chat";
import {
    addNoteToConversation,
    getContactById,
    getContactList,
    getContactListByChannel,
    getContactListByChannels,
    getContactListNullChannel,
    getContactListStats,
    getConversationList,
    loadChatHistoryV2,
    loadOldConversations,
    loadOldConversationsAsPaged as loadOldConversationsAsPagedVal,
    loadOldMessages,
    updateConversationTags,
    updateCustomLoginInfos,
} from "../../api/chat";
import { log2 } from "../../utils/log2";
import { sendSocketMessage } from "../../utils/socketStream";
import * as _ from "lodash";
import store from "../index";
import { PHONE_UTILS } from "../../utils/phone";
import { i18n } from "../../language";
import moment from "moment";
import { updateTagVisitor } from "../../api/visitor";
import SYSTEM_PUBSUB from "../../utils/systemPubSub";
import StringUtils from "../../utils/StringUtils";

const log = log2("ChatStore");
let chatBarIndex = 1;

const defaultState = () => ({
    observing: [],
    conversations: [],
    activeConversation: null,
    previousConversation: null,
    privateMessageConversation: null,
    overdueTime: null,
    activeAgentRateConversationCount: null,
    openChats: [],
    beepPlayedChatIds: [],
    usersPrivateChatChannelMessages: [],
    stats: {
        total: 0,
        free: 0,
        waiting: 0,
        late: 0,
    },
    conversationsMeta: getConversationsMeta() || {},
    myChatIds: getMyChatIds(),
    autoTranslate: [],
    draftMessages: {},
    loadingState: [],
    pinnedReplyToMessages: {},
    lastMessages: {},
});

const chat = {
    namespaced: true,
    state: {
        loadingState: defaultState().loadingState,
        observing: defaultState().observing,
        conversations: defaultState().conversations,
        activeConversation: defaultState().activeConversation,
        fakeActiveConversation: null,
        previousConversation: defaultState().previousConversation,
        privateMessageConversation: defaultState().privateMessageConversation,
        overdueTime: defaultState().overdueTime,
        activeAgentRateConversationCount: defaultState().activeAgentRateConversationCount,
        openChats: defaultState().openChats,
        beepPlayedChatIds: defaultState().beepPlayedChatIds,
        usersPrivateChatChannelMessages: defaultState().usersPrivateChatChannelMessages,
        isChatHistoryOpen: false,
        stats: defaultState().stats,
        latestRows: [],
        latestChatbotChats: [],
        lookedConversationIds: getLookedChatIds(),
        conversationsMeta: defaultState().conversationsMeta,
        myChatIds: defaultState().myChatIds,
        autoTranslate: defaultState().autoTranslate,
        pendingMessages: getPendingMessages(),
        draftMessages: defaultState().draftMessages,
        afkCheckedChatIds: [],
        chatHistory: {},
        pinnedReplyToMessages: {},
        lastMessages: {},
        conversationList: [],
        activeContact: null,
        watchingContactIds: [],
        contactPaginationCursor: {
            stats: {
                all: 0,
                unread: 0,
                waiting: 0,
                myChats: 0,
            },
            offset: {
                skip: 0,
                limit: 25,
            },
        },
        contactLists: [],
        contacts: [],
        previousContactId: null,
        contactListLoading: false,
        contactListFilterMode: "custom", // queue,chatbot
        selectedCustomFilterTemplate: null,
        contactListFilters: {
            timeSelect: "last24h",
            startDate: moment().subtract(1, "days").toISOString(),
            endDate: moment().endOf("day").toISOString(),
            query: "",
            selectedDepartmentIds: [],
            selectedUsers: [],
            selectedChannelIds: [],
            selectedInteractions: [],
            lastChatStatusList: ["active"],
            lastChatOwner: [],
            showUnread: false,
        },
        contactListSortMode: "chat.create.desc", // message.desc , chat.create.asc , chat.create.desc
        contactListGroupBy: "none", // "none,agent,department,channel",
        previousContactListFilters: null,
        multiChatViewerMode: {
            enabled: false,
            type: "agent",
            value: null,
        },
        lastAgentStartedContact: null,
        chatListToolbarLocked: true,
        whatsappContactStatus: {},
    },
    getters: {
        getFittedContactListFilters: (state) => {
            if (state.contactListFilterMode === "custom") {
                return state.contactListFilters;
            } else if (state.contactListFilterMode === "chatbot") {
                return {
                    timeSelect: "last24h",
                    startDate: moment().subtract(1, "days").toISOString(),
                    endDate: moment().endOf("day").toISOString(),
                    query: "",
                    selectedDepartmentIds: [],
                    selectedUsers: [],
                    selectedChannelIds: [],
                    selectedInteractions: [],
                    lastChatStatusList: ["chatbot"],
                    lastChatOwner: [],
                    showUnread: false,
                };
            } else if (state.contactListFilterMode === "queue") {
                return {
                    timeSelect: "last24h",
                    startDate: moment().subtract(1, "days").toISOString(),
                    endDate: moment().endOf("day").toISOString(),
                    query: "",
                    selectedDepartmentIds: [],
                    selectedUsers: [],
                    selectedChannelIds: [],
                    selectedInteractions: [],
                    lastChatStatusList: ["new"],
                    lastChatOwner: [],
                    showUnread: false,
                };
            }
        },
        isDefaultFilter: (state) => {
            return (
                state.contactListFilterMode === "custom" &&
                ["all-agent-inbox", null].includes(state.selectedCustomFilterTemplate)
            );
        },
        getActiveConversationPendingMessages: (state) => {
            if (state.activeContact) {
                return state.pendingMessages.filter(
                    (message) => message.conversationId == state.activeContact.lastConversation._id
                );
            }
            return [];
        },
        getAutoTranslateIsActive: (state) => {
            return state.autoTranslate;
        },
        getActiveConversations: (state) => {
            return state.conversations;
        },
        getObservingConversationIds: (state) => {
            return _.uniq(state.observing);
        },
        getMyActiveConversations: (state, getters, rootState) => {
            const mergedChats = _.uniqBy([...state.conversations, ...state.latestRows], "_id");
            const allChats = mergedChats.map((x) => ({
                ...x,
                meta: state.conversationsMeta[x._id] || {},
            }));
            const myConversations = rootState.auth.user
                ? allChats.filter((c) => c.meta.active === true && c.users.includes(rootState.auth.user._id))
                : [];
            const watching = allChats.filter((x) => state.observing.includes(x._id));
            log("getMyActiveConversations", {
                allChats,
                user: rootState.auth.user,
                myConversations,
                conversations: state.conversations,
                observing: state.observing,
                watching,
            });
            let result = _.uniqBy([...myConversations, ...watching], (q) => q._id);
            result = _.orderBy(result, ["meta.chatBarOrder"], ["asc"]);

            if (
                Math.ceil(result.length / rootState.app.chatBarPageSize) < rootState.app.chatBarCurrentPage &&
                rootState.app.chatBarCurrentPage > 1
            )
                store.dispatch("app/setChatBarCurrentPageChange", rootState.app.chatBarCurrentPage - 1);

            return result;
        },
        getWaitingConversations: (state) => {
            return state.conversations.filter((c) => c.status === "new" || c.users.length === 0);
        },
        getAbandonedConversations: (state) => {
            return state.conversations.filter((c) => ["active", "lost"].includes(c.status) && !c.users.length);
        },
        getUnreadConversations: (state) => {
            return state.conversations.filter((c) => {
                const meta = state.conversationsMeta[c._id] || {};
                return meta.unread > 0;
            });
        },
        activeAgentRate: (state, getters, rootState) => {
            const onlineUsers = rootState.user.users.data.filter(
                (u) => u.isOnline === true && u.status.toLowerCase() === "online"
            );
            const activeConversationsCount = _.sum(onlineUsers.map((x) => x.conversationCount || 0));
            let systemLimit = rootState.setting.settings.autoAssignDefaultLimit;
            systemLimit = Math.max(systemLimit, 0);
            let totalUserMaxCapacity = onlineUsers.map((x) => {
                const userLimit = x.autoAssignLimit;
                let result = 0;
                result = userLimit < 0 ? systemLimit : userLimit;
                return result;
            });
            totalUserMaxCapacity = _.sum(totalUserMaxCapacity);
            if (activeConversationsCount > 0) {
                if (totalUserMaxCapacity === 0) {
                    return 0;
                } else {
                    return (activeConversationsCount / totalUserMaxCapacity) * 100;
                }
            } else {
                return 0;
            }
        },
        systemMaxChatCapacity: (state, getters, rootState) => {
            const onlineUsers = rootState.user.users.data.filter(
                (u) => u.isOnline === true && u.status.toLowerCase() === "online"
            );
            let systemLimit = rootState.setting.settings.autoAssignDefaultLimit;
            systemLimit = systemLimit ? systemLimit : 0;
            systemLimit = Math.max(systemLimit, 0);
            const totalUserMaxCapacity = onlineUsers.map((x) => {
                let userLimit = x.autoAssignLimit;
                let result = 0;
                result = userLimit < 0 ? systemLimit : userLimit;
                return result;
            });

            return _.sum(totalUserMaxCapacity);
        },
        findConversation: (state) => (id) => {
            return state.conversations.find((c) => c._id === id);
        },
        findConversationByVisitorId: (state) => (id) => {
            return state.conversations.find((c) => c.visitor === id);
        },
        findActiveConversation: (state) => (id) => {
            return state.conversations.find((c) => c.active === true && c._id === id);
        },
    },
    mutations: {
        SET_WHATSAPP_CONTACT_STATUS(state, whatsappContactStatus) {
            state.whatsappContactStatus = whatsappContactStatus;
        },
        SET_LAST_AGENT_STARTED_CONTACT(state, contact) {
            log("ChatWatcher SET_LAST_AGENT_STARTED_CONTACT", contact);
            state.lastAgentStartedContact = contact;
        },
        SET_AFK_CHECKED_CHATS(state, chatIds) {
            state.afkCheckedChatIds = chatIds;
        },
        SET_LAST_MESSAGES(state, messages) {
            state.lastMessages = messages;
        },
        UPDATE_INACTIVE_STATE(state, payload) {
            const { conversationId, inactive } = payload;
            log("UPDATE_INACTIVE_STATE", { conversationId, inactive });
            const findIndex = state.conversations.findIndex((c) => c._id === conversationId);
            log("UPDATE_INACTIVE_STATE", { findIndex });
            try {
                if (findIndex !== -1) {
                    const foundConversation = state.conversations[findIndex];
                    foundConversation.inactive = inactive;
                    Vue.set(state.conversations, findIndex, foundConversation);
                    if (
                        state.activeConversation != null &&
                        state.conversations[findIndex]._id === state.activeConversation._id
                    ) {
                        state.activeConversation = state.conversations[findIndex];
                    }
                }
            } catch (e) {
                console.error(e);
            }
        },
        ADD_DRAFT_MESSAGE(state, payload) {
            const { conversationId, message } = payload;
            log("ADD_DRAFT_MESSAGE", { conversationId, message });
            state.draftMessages[conversationId] = message;
        },
        REMOVE_DRAFT_MESSAGE(state, conversationId) {
            if (!!state.draftMessages[conversationId]) delete state.draftMessages[conversationId];
        },
        ADD_PENDING_MESSAGE(state, message) {
            state.pendingMessages.push(message);
            setPendingMessages(state.pendingMessages);
        },
        REMOVE_PENDING_MESSAGE(state, uniqMessageId) {
            state.pendingMessages = state.pendingMessages.filter((x) => x.uniqMessageId !== uniqMessageId);
            setPendingMessages(state.pendingMessages);
        },
        ACT_PENDING_MESSAGE(state, uniqMessageId) {
            state.pendingMessages = state.pendingMessages.filter((x) => x.uniqMessageId !== uniqMessageId);
            setPendingMessages(state.pendingMessages);
        },
        UPDATE_VISITOR_LANG(state, payload) {
            let { conversationId, preferredLang } = payload;
            log("UPDATE_VISITOR_LANG", { conversationId, preferredLang });
            let maybeChat = state.conversations.find((c) => c._id === conversationId);
            if (!!maybeChat) {
                maybeChat.visitor.preferredLang = preferredLang;
                maybeChat.languageCode = preferredLang;
            }
            if (!!state.activeConversation && state.activeConversation._id === conversationId) {
                state.activeConversation.visitor.preferredLang = preferredLang;
                state.activeConversation.languageCode = preferredLang;
            }
        },
        SET_AUTO_TRANSLATE(state, payload) {
            if (payload.state) state.autoTranslate.push(payload.id);
            else state.autoTranslate = state.autoTranslate.filter((x) => x !== payload.id);
        },
        SET_LATEST_ROWS: (state, rows) => {
            state.latestRows = [];
            state.latestRows = rows;
        },
        SET_LATEST_CHATBOT_CHATS: (state, rows) => {
            state.latestChatbotChats = rows;
        },
        SET_CONVERSATION_STATS: (state, stats) => {
            state.stats = stats;
        },
        SET_FAKE_ACTIVE_CONVERSATION: (state, chat) => {
            state.fakeActiveConversation = chat;
        },
        SET_PRIVATE_MESSAGE_CONVERSATION: (state, id) => {
            state.privateMessageConversation = id;
        },
        ADD_CONVERSATION: (state, payload) => {
            let { conversation, meta, observe } = payload;
            if (!meta.chatBarOrder) meta.chatBarOrder = chatBarIndex++;
            log("ADD_CONVERSATION", { conversation, meta: { ...meta } });
            state.conversations = _.uniqBy([...state.conversations, conversation], (q) => q._id);
            const indexOfCon = state.conversations.findIndex((x) => x._id === conversation._id);
            state.conversationsMeta[conversation._id] = meta;
            Vue.set(state.conversationsMeta, conversation._id, meta);
            setConversationsMeta(state.conversationsMeta);
            Vue.set(state.conversations, indexOfCon, conversation);
            if (!observe) state.myChatIds = _.uniq([...state.myChatIds, conversation._id]);
            setMyChatIds(state.myChatIds);
        },
        ADD_MULTI_CONVERSATION: (state, payload) => {
            let { context, addingChats } = payload;
            const { rootState } = context;
            log("ADD_MULTI_CONVERSATION", addingChats);
            log("ADD_MULTI_CONVERSATION before", {
                ids: addingChats.map((x) => _.takeRight(x._id, 3).join("")),
            });
            const grouped = _.groupBy(addingChats, (q) => q._id);
            addingChats = Object.keys(grouped).map((key) => _.last(grouped[key]));
            addingChats
                .filter((x) => x.users.includes(rootState.auth.user._id))
                .forEach((x) => {
                    const meta = state.conversationsMeta[x._id] || {};
                    meta.chatBarOrder = chatBarIndex++;
                    state.conversationsMeta[x._id] = meta;
                    state.myChatIds.push(x._id);
                    setConversationsMeta(state.conversationsMeta);
                });
            state.myChatIds = _.uniq(state.myChatIds);
            setMyChatIds(state.myChatIds);
            state.conversations = _.uniqBy([...state.conversations, ...addingChats], (q) => q._id);
        },
        UPDATE_CONVERSATION: (state, payload) => {
            let { context, conversation } = payload;
            const { rootState } = context;
            const findIndex = state.conversations.findIndex((c) => c._id === conversation._id);
            log("UPDATE_CONVERSATION", { findIndex, ...payload });
            try {
                if (findIndex !== -1) {
                    const foundConversation = state.conversations[findIndex];
                    const meta = state.conversationsMeta[conversation._id] || {};
                    if (!meta.chatBarOrder && conversation.users.includes(rootState.auth.user._id)) {
                        meta.chatBarOrder = chatBarIndex++;
                        state.conversationsMeta[conversation._id] = meta;
                        setConversationsMeta(state.conversationsMeta);
                    } else {
                        Vue.set(state.conversations, findIndex, conversation);
                    }
                }

                if (
                    state.activeConversation != null &&
                    state.conversations[findIndex]._id === state.activeConversation._id
                ) {
                    state.activeConversation = state.conversations[findIndex];
                }

                log("UPDATE_CONVERSATION after", {
                    conversations: state.conversations,
                });
            } catch (e) {
                console.error(e);
            }
        },
        UPDATE_MULTI_CONVERSATION: (state, payload) => {
            let { updatingChats, context } = payload;
            const { rootState } = context;
            const grouped = _.groupBy(updatingChats, (x) => x._id);
            updatingChats = Object.keys(grouped) // aynı id de 2 veya daha fazla update varsa sonuncuyu alıyoruz
                .map((id) => {
                    const items = grouped[id];
                    return items.length === 1 ? items[0] : _.last(items);
                });
            const currentChatIdsMap = state.conversations.reduce((acc, cur) => {
                acc[cur._id] = cur;
                return acc;
            }, {});

            const validUpdatingIds = [];
            const updatingMaps = {};
            updatingChats.forEach((c) => {
                if (currentChatIdsMap[c._id]) {
                    c.isLooked = currentChatIdsMap[c._id].isLooked;
                    validUpdatingIds.push(c._id);
                    updatingMaps[c._id] = c;
                }
            });
            Object.keys(updatingMaps)
                .map((id) => updatingMaps[id])
                .filter((x) => x.users.includes(rootState.auth.user._id) && !x.chatBarOrder)
                .forEach((x) => {
                    const meta = state.conversationsMeta[x._id] || {};

                    meta.chatBarOrder = chatBarIndex++;
                    state.conversationsMeta[x._id] = meta;
                    setConversationsMeta(state.conversationsMeta);
                });

            if (validUpdatingIds.length) {
                state.conversations = _.uniqBy(
                    [
                        ...state.conversations.filter((c) => updatingMaps[c._id] == null),
                        ...Object.keys(updatingMaps).map((key) => updatingMaps[key]),
                    ],
                    (q) => q._id
                );

                if (state.activeConversation != null && updatingMaps[state.activeConversation._id] != null) {
                    state.activeConversation = updatingMaps[state.activeConversation._id];
                }
            }
        },
        REMOVE_CONVERSATION: (state, id) => {
            state.lookedConversationIds = _.uniq(state.lookedConversationIds.filter((c) => c !== id));
            delete state.conversationsMeta[id];
            setConversationsMeta(state.conversationsMeta);
            deleteLookedChatFromStorage(id);
            state.conversations = state.conversations.filter((c) => c._id !== id);
            state.myChatIds = state.myChatIds.filter((cId) => cId !== id);
            setMyChatIds(state.myChatIds);
            if (state.activeConversation && state.activeConversation._id === id) {
                state.activeConversation = null;
            }
        },
        REMOVE_MULTI_CONVERSATION: (state, ids) => {
            state.lookedConversationIds = _.uniq(_.difference(state.lookedConversationIds, ids));
            const idsMap = ids.reduce((acc, cur) => {
                if (state.activeConversation && state.activeConversation._id === cur) {
                    state.activeConversation = null;
                }
                deleteLookedChatFromStorage(cur);
                delete state.conversationsMeta[cur];
                setConversationsMeta(state.conversationsMeta);
                acc[cur] = true;
                return acc;
            }, {});
            state.conversations = state.conversations.filter((c) => !idsMap[c._id]);
            state.myChatIds = state.myChatIds.filter((cId) => !idsMap[cId]);

            setMyChatIds(state.myChatIds);
        },
        SET_ACTIVE_CONVERSATION: (state, id) => {
            if (!id) {
                state.activeConversation = null;
                return;
            }
            const conversation = state.conversations.find((c) => c._id === id);
            if (!conversation) return;
            let meta = state.conversationsMeta[id] || {};
            meta.unread = 0;
            meta.isLooked = true;
            state.lookedConversationIds = _.uniq([state.lookedConversationIds, id]);
            pushLookedChatId(conversation._id);
            if (conversation.languageCode == null) {
                conversation.languageCode = "tr";
            }
            state.activeConversation = conversation;
            state.conversationsMeta[id] = meta;
            setConversationsMeta(state.conversationsMeta);
            Vue.set(state.activeConversation, "active", true);
            state.openChats = _.uniq([...state.openChats, state.activeConversation]);
        },
        SET_PREVIOUS_CONVERSATION: (state, id) => {
            state.previousConversation = id;
        },
        LEAVE_CONVERSATION: (state, id, removeFromBeep = false) => {
            const conversation = state.conversations.find((c) => c._id === id);

            if (conversation) {
                const meta = state.conversationsMeta[id] || {};
                meta.active = false;
                delete meta.chatBarOrder;
                state.conversationsMeta[id] = meta;
                setConversationsMeta(state.conversationsMeta);
                if (state.activeConversation && state.activeConversation._id === id) {
                    state.activeConversation = null;
                }
                if (removeFromBeep) state.beepPlayedChatIds = state.beepPlayedChatIds.filter((x) => x !== id);
            }
        },
        HIDE_CONVERSATION: (state) => {
            state.activeConversation = null;
        },
        ADD_USER_TO_CONVERSATION: (state, payload) => {
            const { userId, conversationId } = payload;
            const findIndex = state.conversations.findIndex((x) => x._id === conversationId);
            if (findIndex !== -1) {
                const conversation = state.conversations[findIndex];
                conversation.users = _.uniq([...(conversation.users || []), userId]);
                if (conversation.status === "lost") {
                    conversation.status = "active";
                }
                Vue.set(state.conversations, findIndex, conversation);
            }
        },
        LOST_CONVERSATION: (state, conversationId) => {
            log.info("LOST_CONVERSATION", { conversationId });
            const findIndex = state.conversations.findIndex((x) => x._id === conversationId);
            if (findIndex !== -1) {
                const conversation = state.conversations[findIndex];
                conversation.status = "lost";
                Vue.set(state.conversations, findIndex, conversation);
            }
        },
        REMOVE_USER_FROM_CONVERSATION: (state, payload) => {
            const { userId, conversationId } = payload;
            const findIndex = state.conversations.findIndex((x) => x._id === conversationId);
            if (findIndex !== -1) {
                const conversation = state.conversations[findIndex];
                conversation.users = conversation.users.filter((id) => id !== userId);
                // if (conversation.users.length === 0 && !!conversation.connectedUser) {
                //     conversation.status = 'lost'
                // }
                Vue.set(state.conversations, findIndex, conversation);
            }
        },

        REMOVE_CHAT_ID: (state, conversationId) => {
            state.myChatIds = state.myChatIds.filter((cId) => cId !== conversationId);
            setMyChatIds(state.myChatIds);
        },
        ADD_OBSERVE: (state, id) => {
            state.observing.push(id);
            const findIndex = state.conversations.findIndex((x) => x._id === id);
            if (findIndex !== -1) {
                const meta = state.conversationsMeta[id] || {};
                meta.chatBarOrder = meta.chatBarOrder || chatBarIndex++;
                state.conversationsMeta[id] = meta;
                setConversationsMeta(state.conversationsMeta);
            }
            state.observing = _.uniq(state.observing);
            setWatchingChatIds(state.observing);
            log("ADD_OBSERVE", id, "last state", state.observing);
        },
        REMOVE_OBSERVE: (state, id) => {
            const isWatching = state.observing.includes(id);
            if (isWatching) {
                state.observing = state.observing.filter((x) => x !== id);
                state.observing = _.uniq(state.observing);
                state.lookedConversationIds = _.uniq(state.lookedConversationIds.filter((x) => x !== id));
                deleteLookedChatFromStorage(id);
                setWatchingChatIds(state.observing);
                const findIndex = state.conversations.findIndex((x) => x._id === id);
                if (findIndex !== -1) {
                    const meta = state.conversationsMeta[id] || {};
                    meta.isLooked = false;
                    delete meta.chatBarOrder;
                    state.conversationsMeta[id] = meta;
                    setConversationsMeta(state.conversationsMeta);
                }
                log("REMOVE_OBSERVE", id, "last state", state.observing);
            }
        },
        REMOVE_ACTIVE_CONVERSATION: (state) => {
            log("REMOVE_ACTIVE_CONVERSATION");
            state.activeConversation = null;
        },
        REMOVE_MULTI_OBSERVE: (state, ids) => {
            state.lookedConversationIds = _.uniq(_.difference(state.lookedConversationIds, ids));
            const idsMap = ids.reduce((acc, cur) => {
                acc[cur] = true;
                deleteLookedChatFromStorage(cur);
                return acc;
            }, {});
            state.observing = state.observing.filter((id) => !idsMap[id]);
            state.observing = _.uniq(state.observing);
            setWatchingChatIds(state.observing);
        },
        REMOVE_AGENT: (state, payload) => {
            const conversation = state.conversations.find((c) => c._id === payload.conversation);
            if (!!conversation) {
                conversation.lastConversation.users = conversation.lastConversation.users.filter(
                    (id) => id !== payload.agent
                );
            }
        },
        SET_BEEP_CONVERSATION: (state, payload) => {
            state.beepPlayedChatIds.push(payload.id);
        },
        SET_MULTI_BEEP_CONVERSATION: (state, ids) => {
            log.debug("SET_MULTI_BEEP_CONVERSATION", { ids });
            state.beepPlayedChatIds = _.uniq([...state.beepPlayedChatIds, ...ids]);
        },
        ADD_USERS_PRIVATE_CHANNEL_MESSAGE: (state, message) => {
            log.debug("ADD_USERS_PRIVATE_CHANNEL_MESSAGE", { message });
            state.usersPrivateChatChannelMessages = [...state.usersPrivateChatChannelMessages, message];
        },
        DELETE_PRIVATE_CHANNEL_MESSAGE: (state, messageId) => {
            log.debug("DELETE_PRIVATE_CHANNEL_MESSAGE", { messageId });
            state.usersPrivateChatChannelMessages = state.usersPrivateChatChannelMessages.reduce(
                (messages, currMessage) => {
                    if (currMessage.createdDate === messageId) {
                        currMessage = {
                            ...currMessage,
                            message: i18n.t("message.messageDeleted"),
                        };
                        messages.push(currMessage);
                        return messages;
                    }
                    messages.push(currMessage);
                    return messages;
                },
                []
            );
        },
        ADD_MULTI_USERS_PRIVATE_CHANNEL_MESSAGE: (state, messages) => {
            state.usersPrivateChatChannelMessages = messages;
        },
        /**
         * Admin giriş yaptığında resetlemek için çağırıyoruz.
         * @param state
         */
        RESET_STATE: (state) => {
            log.info("RESET_STATE running");
            state.observing = defaultState().observing;
            state.conversations = defaultState().conversations;
            state.activeConversation = defaultState().activeConversation;
            state.previousConversation = defaultState().previousConversation;
            state.privateMessageConversation = defaultState().privateMessageConversation;
            state.overdueTime = defaultState().overdueTime;
            state.activeAgentRateConversationCount = defaultState().activeAgentRateConversationCount;
            state.openChats = defaultState().openChats;
            state.beepPlayedChatIds = defaultState().beepPlayedChatIds;
            state.usersPrivateChatChannelMessages = defaultState().usersPrivateChatChannelMessages;
            state.myChatIds = defaultState().myChatIds;
            state.conversationsMeta = {};
            setConversationsMeta({});
            setMyChatIds(state.myChatIds);
        },
        TOGGLE_CHAT_HISTORY: (state, payload) => {
            state.isChatHistoryOpen = payload || !state.isChatHistoryOpen;
        },
        SET_CONVERSATIONS: (state, payload) => {
            const { conversations, partNumber, userId } = payload;
            if (partNumber === 1) {
                if (conversations.length === 0) {
                    const isDeleteNeedForActiveConversation = _.get(state, "activeConversation.users", []).includes(
                        userId
                    );
                    if (isDeleteNeedForActiveConversation) {
                        state.activeConversation = null;
                    }
                    state.conversations = [];
                    state.conversationsMeta = {};
                    state.myChatIds = [];
                    setConversationsMeta(state.conversationsMeta);
                    setMyChatIds(state.myChatIds);
                } else {
                    const newMetas = conversations.reduce((acc, cur) => {
                        log("SET_CONVERSATIONS", { cur });
                        const maybeMeta = state.conversationsMeta[cur._id];
                        if (!maybeMeta) {
                            acc[cur._id] = {
                                active: true,
                                autoAssign: false,
                                unread: 0,
                                chatBarOrder: chatBarIndex++,
                                isLooked: false,
                            };
                        }
                        return acc;
                    }, {});
                    state.conversationsMeta = {
                        ...state.conversationsMeta,
                        ...newMetas,
                    };
                    state.conversations = conversations;

                    _.difference(
                        Object.keys(state.conversationsMeta),
                        conversations.map((x) => x._id)
                    ).forEach((id) => {
                        delete state.conversationsMeta[id];
                    });
                    _.difference(
                        state.myChatIds,
                        conversations.map((x) => x._id)
                    ).forEach((id) => {
                        state.myChatIds = state.myChatIds.filter((cId) => cId !== id);
                    });

                    setConversationsMeta(state.conversationsMeta);
                    setMyChatIds(state.myChatIds);
                }
            } else {
                state.conversations = _.concat(state.conversations, conversations);
            }
        },
        UPDATE_META: (state, payload) => {
            let { meta, conversationId } = payload;
            // state.conversationsMeta[conversationId] = meta
            Vue.set(state.conversationsMeta, conversationId, meta);
            state.conversations = [...state.conversations];
            setConversationsMeta(state.conversationsMeta);
        },
        UPDATE_CHAT_HISTORY: (state, message) => {
            let { conversationId, payload } = message;
            Vue.set(state.chatHistory, conversationId, payload);
            try {
                if (Object.keys(state.chatHistory).length > 10) {
                    const leastUsed = _.orderBy(
                        Object.keys(state.chatHistory).map((key) => state.chatHistory[key]),
                        ["ts"],
                        ["asc"]
                    )[0];
                    if (leastUsed) {
                        Vue.delete(state.chatHistory, leastUsed._id);
                    }
                }
            } catch (e) {}
        },
        ADD_MESSAGE_TO_CHAT_HISTORY: (state, { conversationId, message }) => {
            let chatHistory = state.chatHistory[conversationId];
            if (chatHistory) {
                chatHistory.data = [...chatHistory.data, message];
                chatHistory.offset = chatHistory.offset + 1;
                chatHistory.totalCount = chatHistory.totalCount + 1;
                chatHistory.totalPage = Math.ceil(chatHistory.totalCount / chatHistory.limit);
                Vue.set(state.chatHistory, conversationId, chatHistory);
            }
        },
        RESET_CHAT_HISTORY: (state) => {
            state.chatHistory = {};
        },
        UPDATE_MESSAGE_FROM_HISTORY: (state, { conversationId, message }) => {
            state.chatHistory = Object.keys(state.chatHistory).reduce((acc, cur) => {
                if (cur === conversationId) {
                    acc[cur].data = acc[cur].data.map((x) => {
                        if (x._id === message._id) {
                            return message;
                        }
                        return x;
                    });
                }
                return acc;
            }, state.chatHistory);
        },
        SET__MESSAGE_DELETED_FROM_HISTORY: (state, { conversationId, uniqMessageId }) => {
            console.log("SET__MESSAGE_DELETED_FROM_HISTORY", { conversationId, uniqMessageId });
            state.chatHistory = Object.keys(state.chatHistory).reduce((acc, cur) => {
                if (cur === conversationId) {
                    acc[cur].data = acc[cur].data.map((x) => {
                        if (x.uniqMessageId === uniqMessageId) {
                            x.isDeleted = true;
                            return _.cloneDeep(x);
                        }
                        return x;
                    });
                }
                return acc;
            }, state.chatHistory);
            state.chatHistory = _.cloneDeep(state.chatHistory);
        },
        SET_REPRESENTATIVE_NOTE: (state, payload) => {
            state.activeConversation.note = payload.note;
        },
        UPDATE_VISITOR_DETAIL: (state, { conversationId, fullname, email }) => {
            log("UPDATE_VISITOR_DETAIL", { conversationId, fullname, email });
            state.conversations = state.conversations.map((x) => {
                if (x._id === conversationId) {
                    x.fullname = fullname;
                    x.email = email;
                }
                return x;
            });
            if (state.activeConversation && state.activeConversation._id === conversationId) {
                state.activeConversation.fullname = fullname;
                state.activeConversation.email = email;
            }
        },
        UPDATE_VISITOR_URL: (state, payload) => {
            log("UPDATE_VISITOR_URL", payload);
            state.conversations = state.conversations.map((x) => {
                if (x._id === payload.conversationId) {
                    x.visitingPage = payload.url;
                }
                return x;
            });
            if (state.activeConversation && state.activeConversation._id === payload.conversationId) {
                state.activeConversation.visitingPage = payload.url;
            }
        },
        UPDATE_TAGS: (state, conversation) => {
            state.activeConversation.tags = conversation.tags;
        },
        UPDATE_VISITOR_TAGS: (state, tags) => {
            state.activeConversation.visitor.tags = tags;
        },
        UPDATE_CHAT_VISITOR_PROFILE: (state, { visitor, updatingFields }) => {
            state.conversationId = state.conversations.map((conversation) => {
                if (conversation.visitor._id === visitor._id) {
                    conversation.visitor = {
                        ...conversation.visitor,
                        ...updatingFields.map((field) => visitor[field]),
                    };
                }
                return conversation;
            });
        },

        UPDATE_CUSTOM_LOGIN_INFOS: (state, conversation) => {
            state.activeConversation.customLoginInfos = conversation.customLoginInfos;
        },

        SET_LOADING_FILE: (state, payload) => {
            if (state.loadingState.length === 0) {
                state.loadingState.push(payload);
            } else {
                state.loadingState.map((p) => {
                    if (p.chat_id === payload.chat_id && p.fileNumber === payload.fileNumber) {
                        payload.percent = p.percent;
                    } else {
                        state.loadingState.push(payload);
                    }
                });
            }
        },
        PIN_REPLY_TO_MESSAGE: (state, message) => {
            const chatId = message.raw.conversationId || message.raw.conversation;
            state.pinnedReplyToMessages[chatId] = message;
            state.pinnedReplyToMessages = _.cloneDeep(state.pinnedReplyToMessages);
        },
        UNPIN_REPLY_TO_MESSAGE: (state, conversationId) => {
            delete state.pinnedReplyToMessages[conversationId];
            state.pinnedReplyToMessages = _.cloneDeep(state.pinnedReplyToMessages);
        },
        SET_CONTACT_LIST: (state, items) => {
            if (state.contactListSortMode === "message.desc") {
                items = items.sort(
                    (a, b) =>
                        (b.lastMessage && new Date(b.lastMessage.createdDate)) -
                        (a.lastMessage && new Date(a.lastMessage.createdDate))
                );
            } else if (state.contactListSortMode === "chat.create.desc") {
                items = items.sort((a, b) => {
                    const date1 = a.lastConversation.createdDate || a.lastInteractionDate;
                    const date2 = b.lastConversation.createdDate || b.lastInteractionDate;
                    return new Date(date2) - new Date(date1);
                });
            } else if (state.contactListSortMode === "chat.create.asc") {
                items = items.sort((a, b) => {
                    const date1 = a.lastConversation.createdDate || a.lastInteractionDate;
                    const date2 = b.lastConversation.createdDate || b.lastInteractionDate;
                    return new Date(date1) - new Date(date2);
                });
            }
            state.contactLists = items.sort((a, b) => b.vipCustomer - a.vipCustomer);
            if (state.activeContact) {
                const currentActiveContact = state.activeContact;
                const contactFromList = items.find((x) => x._id === currentActiveContact._id);
                if (contactFromList) {
                    const isChanged =
                        StringUtils.hashCodeObj(currentActiveContact) !== StringUtils.hashCodeObj(contactFromList);
                    if (isChanged) {
                        log.warn("ChatWatcher active contact model change detected in SET_CONTACT_LIST");
                        state.activeContact = contactFromList;
                    }
                } else {
                    state.activeContact = null;
                }
            }
            if (state.lastAgentStartedContact) {
                const viewingContact = items.find((x) => x._id === state.lastAgentStartedContact.contactId);
                if (viewingContact) {
                    state.activeContact = viewingContact;
                    state.lastAgentStartedContact = null;
                }
            }
        },
        SET_CONTACT_PAGINATION_STATS: (state, total) => {
            log("ChatWatcher.SET_CONTACT_PAGINATION_STATS", total);
            state.contactPaginationCursor.stats = total;
        },
        SET_CONTACT_PAGINATION_OFFSET: (state, offset) => {
            state.contactPaginationCursor.offset = offset;
        },
        SET_CONTACT_LIST_FILTER_MODE: (state, mode) => {
            state.contactListFilterMode = mode;
        },
        SET_SELECTED_CUSTOM_FILTER_TEMPLATE: (state, template) => {
            state.selectedCustomFilterTemplate = template;
        },
        SET_CONTACT_LIST_SORT_MODE: (state, mode) => {
            state.contactListSortMode = mode;
            if (mode === "message.desc") {
                state.contactList = state.contactLists.sort(
                    (a, b) => new Date(b.lastInteractionDate) - new Date(a.lastInteractionDate)
                );
            } else if (mode === "chat.create.desc") {
                state.contactList = state.contactLists.sort(
                    (a, b) => new Date(b.lastInteractionDate) - new Date(a.lastInteractionDate)
                );
            } else if (mode === "chat.create.asc") {
                state.contactList = state.contactLists.sort(
                    (a, b) => new Date(a.lastInteractionDate) - new Date(b.lastInteractionDate)
                );
            }
        },
        SET_CONTACT_LIST_GROUP_BY: (state, groupBy) => {
            state.contactListGroupBy = groupBy;
        },
        //multiChatViewerMode
        SET_MULTI_CHAT_VIEWER_MODE: (state, data) => {
            state.multiChatViewerMode = data;
        },
        SET_CONTACT_LIST_FILTERS: (state, filters) => {
            console.log("SET_CONTACT_LIST_FILTERS", filters);
            state.previousContactListFilters = _.cloneDeep(state.contactListFilters);
            state.contactListFilters = filters;
        },
        SET_ACTIVE_CONTACT: (state, contact) => {
            log("SET_ACTIVE_CONTACT", contact);
            state.activeContact = contact;
        },
        SET_WATCHING_CONTACT: (state, contacts) => {
            state.watchingContactIds = contacts;
        },
        SET_CONTACTS: (state, contacts) => {
            log("SET_CONTACTS", contacts);

            state.contacts = contacts;
            if (contacts.length > 200) {
                //cache temizlemek için ekledim
                const contactIdMap = contacts.reduce((acc, cur) => {
                    acc[cur._id] = true;
                    return acc;
                }, {});
                const randomId = _.shuffle(Object.keys(contactIdMap))[0];
                if (randomId) {
                    state.contacts = state.contacts.filter((x) => x._id !== randomId);
                }
            }
        },
        SET_CONTACT_LIST_LOADING: (state, loading) => {
            state.contactListLoading = loading;
        },
        SET_PREVIOUS_CONTACT_ID: (state, contactId) => {
            state.previousContactId = contactId;
        },
        SET_CHAT_LIST_TOOLBAR_LOCKED: (state, locked) => {
            state.chatListToolbarLocked = locked;
        },
    },
    actions: {
        setWhatsappContactStatus({ commit, state }, { whatsappNumber, status }) {
            commit("SET_WHATSAPP_CONTACT_STATUS", {
                ...state.whatsappContactStatus,
                [whatsappNumber]: status,
            });
        },
        setLastMessage({ commit, state }, message) {
            const beforeMessage = state.lastMessages[message.conversationId];
            if (!beforeMessage || moment(message.createdDate).isAfter(beforeMessage.createdDate)) {
                commit("SET_LAST_MESSAGES", {
                    ...state.lastMessages,
                    [message.conversationId]: message,
                });
            }
        },
        setLoadingState({ commit }, payload) {
            log("setLoadingState", payload);
            commit("SET_LOADING_FILE", payload);
        },
        getLoadingState({ state }, fileNumber) {
            log("getLoadingState", fileNumber);
            const loadingFile = state.loadingState.find((p) => p.fileNumber === fileNumber);
            state.loadingState = state.loadingState.filter((p) => p.fileNumber !== fileNumber);
            return loadingFile ? loadingFile.chat_id : "";
        },
        getLoadingFilePercent({ state }, chat_id) {
            log("getLoadingState", chat_id);
            const loadingFile = state.loadingState.find((p) => p.chat_id === chat_id);
            return loadingFile ? loadingFile.percent : "";
        },
        updateInactiveState({ commit }, { conversationId, inactive }) {
            log("updateInactiveState", { conversationId, inactive });
            commit("UPDATE_INACTIVE_STATE", { conversationId, inactive });
        },
        addDraftMessage({ commit }, { conversationId, message }) {
            log("addDraftMessage", { conversationId, message });
            commit("ADD_DRAFT_MESSAGE", { conversationId, message });
        },
        removeDraftMessage({ commit }, conversationId) {
            commit("REMOVE_DRAFT_MESSAGE", conversationId);
        },
        addPendingMessage({ commit }, payload) {
            payload.messageType = payload.messageType ? payload.messageType : "user";
            payload.user = payload.userId;
            payload.status = "pending";
            payload.createdDate = moment().toISOString();
            commit("ADD_PENDING_MESSAGE", payload);
        },
        setLatestRows({ commit }, rows) {
            commit("SET_LATEST_ROWS", rows);
        },
        setLatestChatBotChats({ commit }, rows) {
            commit("SET_LATEST_CHATBOT_CHATS", rows);
        },
        viewFakeConversation({ commit }, chat) {
            commit("SET_FAKE_ACTIVE_CONVERSATION", chat);
        },
        beepConversation({ commit }, payload) {
            commit("SET_BEEP_CONVERSATION", payload);
        },
        multiBeepConversation({ commit }, ids) {
            commit("SET_MULTI_BEEP_CONVERSATION", ids);
        },
        addConversation({ commit, state, rootState, getters }, payload) {
            let { conversation, meta } = payload;
            commit("ADD_CONVERSATION", payload);
            let myActiveConversations = getters.getMyActiveConversations;
            store.dispatch(
                "app/setChatBarCurrentPageChange",
                Math.ceil(myActiveConversations.length / rootState.app.chatBarPageSize)
            );
            if (conversation.forceOpen) {
                commit("SET_ACTIVE_CONVERSATION", conversation._id);
            }
        },
        syncConversationWithIds({ rootState, commit, getters, state }, ids) {
            const allIds = state.conversations.map((x) => x._id).filter((id) => !state.observing.includes(id));
            let deadIds = _.difference(allIds, ids);

            if (deadIds.length) {
                log("DEAD CHAT IDS DETECTED", deadIds);
                commit("REMOVE_MULTI_CONVERSATION", deadIds);
            }
        },
        updateConversation({ commit, rootState }, conversation) {
            commit("UPDATE_CONVERSATION", {
                conversation,
                context: { rootState },
            });
        },
        viewConversation({ rootState, commit, getters, state }, payload) {
            if (state.activeConversation && payload.id !== state.activeConversation._id) {
                commit("SET_PREVIOUS_CONVERSATION", state.activeConversation._id);
            } else {
                commit("SET_PREVIOUS_CONVERSATION", payload._id);
            }
            commit("SET_ACTIVE_CONVERSATION", payload.id);
        },
        joinConversation({ rootState, commit, getters, state }, payload) {
            log("joinConversation", payload);
            const site = rootState.site.site;
            const { conversation, reconnect, observe, autoAssign, minimized } = payload;
            if (observe) {
                commit("ADD_OBSERVE", conversation._id);
            } else {
                commit("REMOVE_OBSERVE", conversation._id);
                conversation.users = _.uniq([...conversation.users, rootState.auth.user._id]);
            }
            if (state.activeConversation && conversation._id !== state.activeConversation._id) {
                commit("SET_PREVIOUS_CONVERSATION", state.activeConversation._id);
            } else {
                commit("SET_PREVIOUS_CONVERSATION", payload._id);
            }
            const sendToSocket = !(payload.hasOwnProperty("sendToSocket") && !payload.sendToSocket);
            if (sendToSocket)
                sendSocketMessage("user join-conversation", {
                    userId: rootState.auth.user._id,
                    conversationId: conversation._id,
                    siteId: site._id,
                    reconnect,
                    autoAssign: autoAssign,
                    watchMode: observe,
                });
            const maybeConversation = state.conversations.find((x) => x._id === conversation._id);
            const meta = state.conversationsMeta[conversation._id] || {};
            log("joinConversation", { maybeConversation });
            meta.chatBarOrder = meta.chatBarOrder || chatBarIndex++;
            if (payload.hasOwnProperty("autoAssign") && payload.autoAssign) {
                meta.autoAssign = true;
            } else if (conversation.hasOwnProperty("autoAssign") && conversation.autoAssign) {
                meta.autoAssign = false;
            }
            log("join conservation.... ", { order: conversation.chatBarOrder });
            meta.active = true;
            commit("ADD_CONVERSATION", { conversation, meta, observe });
            if (!minimized) {
                commit("SET_ACTIVE_CONVERSATION", conversation._id);
            } else {
                meta.active = true;
                commit("ADD_CONVERSATION", { conversation, meta });
            }
        },
        leaveConversation({ state, rootState, commit, getters }, id, removeFromBeep = false) {
            log("leaveConversation", id);
            const site = rootState.site.site;
            const chat = getters.findConversation(id);
            const watchMode = state.observing.includes(id);
            commit("LEAVE_CONVERSATION", id, removeFromBeep);
            commit("REMOVE_OBSERVE", id);
            commit("REMOVE_AGENT", {
                conversation: id,
                agent: rootState.auth.user._id,
            });

            if (chat && chat.status === "destroyed") commit("REMOVE_CONVERSATION", id);
            sendSocketMessage("user leave-chat", {
                userId: rootState.auth.user._id,
                conversationId: id,
                siteId: site._id,
                watchMode: watchMode,
            });
            commit("REMOVE_CONVERSATION", id);
        },
        endConversation({ state, rootState, commit, dispatch, getters }, id) {
            log("endConversation", { id });
            const site = rootState.site.site;
            commit("LEAVE_CONVERSATION", id);
            commit("REMOVE_CONVERSATION", id);
            commit("REMOVE_OBSERVE", id);

            if (state.activeConversation && state.activeConversation._id === id) {
                commit("SET_ACTIVE_CONVERSATION", null);
            }

            sendSocketMessage("user destroy-chat", {
                conversationId: id,
                userId: rootState.auth.user._id,
                siteId: site._id,
            });

            if (rootState.webrtc.dialingConversationId === id) {
                const phone = PHONE_UTILS.getPhone();
                if (phone) {
                    Object.keys(phone.conversations).forEach((number) => phone.hangup(number));
                    phone.camera.stop();
                }
            }
        },
        hideConversation({ commit }, id) {
            commit("HIDE_CONVERSATION", id);
        },
        addUserToConversation({ state, rootState, commit, getters }, payload) {
            commit("ADD_USER_TO_CONVERSATION", payload);
        },
        removeUserFromConversation({ state, rootState, commit, getters }, payload) {
            const { userId, conversationId } = payload;
            commit("REMOVE_USER_FROM_CONVERSATION", payload);
            if (userId === rootState.auth.user._id) {
                commit("REMOVE_CHAT_ID", conversationId);
            }
            const c = state.conversations.find((x) => x._id === conversationId);
            if (!!c) {
                if (c.users.length === 0) {
                    const lostMarkingWaitTime = rootState.setting.settings.overdueTime * 1000;
                    setTimeout(() => {
                        const chat = state.conversations.find((x) => x._id === conversationId);
                        if (!!chat && chat.users.length === 0) {
                            commit("LOST_CONVERSATION", conversationId);
                        }
                    }, lostMarkingWaitTime);
                }
            }
        },
        removeConversation({ state, rootState, commit, getters }, id) {
            log("removeConversation called -> ", id);
            commit("REMOVE_CONVERSATION", id);
            commit("REMOVE_OBSERVE", id);
            if (state.previousConversation === id) commit("SET_PREVIOUS_CONVERSATION", null);

            if (rootState.webrtc.dialingConversationId === id) {
                const phone = PHONE_UTILS.getPhone();
                if (phone) {
                    Object.keys(phone.conversations).forEach((number) => phone.hangup(number));
                    phone.camera.stop();
                }
            }
        },
        removeMultiConversation({ state, rootState, commit, getters }, ids = []) {
            log("removeMultiConversation called -> ", { ids });
            commit("REMOVE_MULTI_CONVERSATION", ids);
            commit("REMOVE_MULTI_OBSERVE", ids);
            if (state.activeConversation && ids.includes(state.activeConversation._id)) {
                commit("REMOVE_ACTIVE_CONVERSATION");
            }

            if (ids.includes(state.previousConversation)) commit("SET_PREVIOUS_CONVERSATION", null);

            if (ids.includes(rootState.webrtc.dialingConversationId)) {
                const phone = PHONE_UTILS.getPhone();
                if (phone) {
                    Object.keys(phone.conversations).forEach((number) => phone.hangup(number));
                    phone.camera.stop();
                }
            }
        },
        banConversation({ state, rootState, commit, getters }, id) {
            const site = rootState.site.site;
            const conversation = state.conversations.find((c) => c._id.toString() === id.toString());
            const visitorId = conversation.visitor._id;
            const socketMessageId = rootState.auth.adminUser != null ? "admin visitor_block" : "user visitor-block";
            sendSocketMessage(socketMessageId, {
                visitorId: visitorId,
                conversationId: id,
                userId: rootState.auth.user._id,
                siteId: site._id,
            });

            commit("LEAVE_CONVERSATION", id);
            commit("REMOVE_CONVERSATION", id);
        },
        loadOldConversations(context, visitor) {
            if ("object" === typeof visitor && visitor.hasOwnProperty("_id")) {
                visitor = visitor._id;
            }

            return loadOldConversations(visitor);
        },
        loadOldConversationsAsPaged(context, { visitor, page, perPage, conversationId }) {
            if ("object" === typeof visitor && visitor.hasOwnProperty("_id")) {
                visitor = visitor._id;
            }

            return loadOldConversationsAsPagedVal(visitor, page, perPage, conversationId);
        },
        loadOldMessages(context, conversationId) {
            return loadOldMessages(conversationId);
        },
        addMessageToChatHistory({ state, commit }, { conversationId, message }) {
            commit("ADD_MESSAGE_TO_CHAT_HISTORY", { conversationId, message });
        },
        resetChatHistory({ state, commit }) {
            commit("RESET_CHAT_HISTORY");
        },
        updateMessageFromHistory({ state, commit }, { conversationId, message }) {
            commit("UPDATE_MESSAGE_FROM_HISTORY", { conversationId, message });
        },
        setMessageAsDeletedFromHistory({ state, commit }, { conversationId, uniqMessageId }) {
            commit("SET__MESSAGE_DELETED_FROM_HISTORY", { conversationId, uniqMessageId });
        },
        async loadLastNMessage({ state, commit, getters }, { conversationId, take }) {
            return loadChatHistoryV2(conversationId, 0, take, [], moment().valueOf())
                .then((res) => res.data?.data || [])
                .catch((err) => []);
        },
        async loadOldHistoryMessages(
            { state, commit, getters },
            { conversationId, cache = false, PREFERRED_LIMIT = 20 }
        ) {
            const cachedData = state.chatHistory[conversationId];
            if (cache && !!cachedData) {
                return cachedData.data;
            }
            const request = state.chatHistory[conversationId] || {
                data: [],
                totalPage: 0,
                totalCount: 0,
                offset: -PREFERRED_LIMIT,
                limit: PREFERRED_LIMIT,
                cachedChatIds: [],
                ts: moment().valueOf(),
                loading: false,
            };
            commit("UPDATE_CHAT_HISTORY", {
                conversationId,
                payload: {
                    ...request,
                    loading: true,
                },
            });
            const nextOffset =
                request.data.length && request.offset + request.limit > request.totalCount
                    ? request.data.length
                    : request.offset + request.limit;

            const { data, totalPage, totalCount, offset, limit } = await loadChatHistoryV2(
                conversationId,
                nextOffset,
                PREFERRED_LIMIT,
                request.cachedChatIds,
                request.ts
            )
                .then((response) => response.data)
                .catch((err) => {
                    commit("UPDATE_CHAT_HISTORY", {
                        conversationId,
                        payload: {
                            ...request,
                            loading: false,
                        },
                    });
                });
            const mergedData = _.chain(request.data)
                .concat(data)
                .uniqBy("uniqMessageId")
                .orderBy("createdDate", "desc")
                .value();
            let cachedChatIds = _.uniq(
                mergedData.map((x) => x.conversation).filter((x) => x !== conversationId && !!x)
            );
            cachedChatIds = []; // cached chat ignoring
            const totalReceived = mergedData.filter((x) => x.messageType !== "conversation-separator").length;
            const isCompleted = totalReceived >= totalCount;

            let lastTS = _.get(_.orderBy(mergedData, "createdDate", "desc"), "0.createdDate");
            lastTS = lastTS ? moment(lastTS).valueOf() : lastTS;
            log("loadOldHistoryMessages", {
                conversationId,
                nextOffset,
                request,
                isCompleted,
                totalReceived,
                totalCount,
                cachedChatIds,
                response: {
                    data,
                    totalPage,
                    offset,
                    limit,
                },
            });
            commit("UPDATE_CHAT_HISTORY", {
                conversationId,
                payload: {
                    data: mergedData,
                    totalPage,
                    totalCount,
                    offset: nextOffset,
                    limit: PREFERRED_LIMIT,
                    cachedChatIds,
                    ts: lastTS,
                    loading: false,
                },
            });
            return mergedData;
        },
        increaseUnreadMessageCount({ state, commit, getters }, payload) {
            const meta = state.conversationsMeta[payload.conversationId];

            if (meta) {
                meta.unread = meta.unread ? meta.unread + 1 : 1;
                meta.lastMessageTime = Date.now();
                commit("UPDATE_META", {
                    meta,
                    conversationId: payload.conversationId,
                });
            }
        },
        setPrivateMessageConversation({ commit }, id) {
            commit("SET_PRIVATE_MESSAGE_CONVERSATION", id);
        },
        resetState({ state, commit }) {
            log.warn("ChatStore state resetting");
            commit("RESET_STATE");
        },
        addUsersPrivateChannelMessage({ state, commit }, message) {
            commit("ADD_USERS_PRIVATE_CHANNEL_MESSAGE", message);
        },
        deleteUsersPrivateChannelMessage({ state, commit }, messageId) {
            commit("DELETE_PRIVATE_CHANNEL_MESSAGE", messageId);
        },
        addMultiUsersPrivateChannelMessage({ state, commit }, messages) {
            commit("ADD_MULTI_USERS_PRIVATE_CHANNEL_MESSAGE", messages);
        },
        toggleChatHistory({ commit }, payload) {
            commit("TOGGLE_CHAT_HISTORY", payload);
        },
        setConversations({ commit, rootState }, payload) {
            const userId = _.get(rootState, "auth.user._id");
            payload.userId = userId;
            commit("SET_CONVERSATIONS", payload);
        },
        setConversationStats({ commit }, stats) {
            commit("SET_CONVERSATION_STATS", stats);
        },
        addNoteToConversation({ commit }, payload) {
            return addNoteToConversation(payload.id, payload.representativeNote, payload.representativeName).then(
                (response) => {
                    if (!payload.isArchive) {
                        commit("SET_REPRESENTATIVE_NOTE", response.data);
                    }
                }
            );
        },
        updateVisitorDetail({ commit }, payload) {
            commit("UPDATE_VISITOR_DETAIL", payload);
        },
        updateTags({ commit }, { conversationId, tagIds }) {
            updateConversationTags(conversationId, tagIds).then(({ data }) => {
                commit("UPDATE_TAGS", data);
            });
        },
        updateVisitorTags({ commit }, { conversationId, visitorId, tagIds }) {
            return updateTagVisitor({ visitorId, tagIds }).then(({ data }) => {
                commit("UPDATE_VISITOR_TAGS", data);
                return data;
            });
        },
        updateChatVisitorProfile({ commit }, { visitor, updatingFields }) {
            commit("UPDATE_CHAT_VISITOR_PROFILE", { visitor, updatingFields });
        },
        updateCustomLoginInfo({ commit }, { conversationId, customLoginInfos }) {
            return updateCustomLoginInfos(conversationId, customLoginInfos);
        },
        async getContactList({ commit }, { pageCount, perPage, searchValue }) {
            return await getContactList(pageCount, perPage, searchValue).then(({ data }) => {
                return data;
            });
        },
        async getContactListByChannel({ commit }, { channel, pageCount, perPage, searchValue }) {
            return await getContactListByChannel(channel, pageCount, perPage, searchValue).then(({ data }) => {
                return data;
            });
        },
        async getContactListByChannels({ commit }, { channelIds, platform, pageCount, perPage, searchValue }) {
            return await getContactListByChannels(channelIds, pageCount, perPage, searchValue, platform).then(
                ({ data }) => {
                    return data;
                }
            );
        },
        async getContactListNullChannel({ commit }, { pageCount, perPage }) {
            return await getContactListNullChannel(pageCount, perPage).then(({ data }) => {
                return data;
            });
        },
        pinReplyToMessage({ commit }, message) {
            commit("PIN_REPLY_TO_MESSAGE", message);
        },
        unpinReplyToMessage({ commit }, conversationId) {
            commit("UNPIN_REPLY_TO_MESSAGE", conversationId);
        },
        // new v2
        setContactLoading({ commit }, loading) {
            commit("SET_CONTACT_LIST_LOADING", loading);
        },
        getConversationList({ state, getters }) {
            const filters = getters.getFittedContactListFilters;
            const paginationCursor = state.contactPaginationCursor.offset;
            paginationCursor.skip = state.contactLists.length;
            const lastContactInteractionDate = state.contactLists.length
                ? state.contactLists[state.contacts.length - 1]?.lastInteractionDate
                : null;
            return getConversationList({
                filters,
                cursor: paginationCursor,
                lastContactInteractionDate,
            });
        },
        getContactListStats({ commit, state, getters }) {
            const filters = getters.getFittedContactListFilters;
            return getContactListStats(filters).then((res) => {
                commit("SET_CONTACT_PAGINATION_STATS", res.data);
            });
        },
        setContactListStats({ commit, state, getters }, stats) {
            commit("SET_CONTACT_PAGINATION_STATS", stats);
        },
        findContact({ state }, contactId) {
            return state.contactLists.find((c) => c._id === contactId);
        },
        async isShowingContactList({ state, getters }, contactId) {
            const filters = getters.getFittedContactListFilters;
            const defaultShowingFilterTemplates = ["all-inbox"];
            let isShowing = defaultShowingFilterTemplates.includes(state.selectedCustomFilterTemplate);
            if (!isShowing) {
                isShowing = await getConversationList({
                    filters: {
                        ...filters,
                        contactId,
                    },
                }).then((res) => !!_.get(res, "data.0"));
            }
            return isShowing;
        },
        async getContactById({ state, commit }, { contactId, cache = true, ignoreSource }) {
            const contact = state.contacts.find((c) => c._id === contactId);
            if (cache && contact) {
                log("ChatWatcher.getContactById cache hit", contactId);
                return contact;
            }
            log("ChatWatcher.getContactById cache miss", contactId);
            return getContactById(contactId, ignoreSource).then((res) => {
                const data = { inited: true, ...res.data };
                const formated = state.contactLists.map((c) => {
                    if (c._id === res.data._id) {
                        data.lastInteractionDate = c.lastInteractionDate;
                        return data;
                    }
                    return c;
                });
                commit("SET_CONTACT_LIST", formated);
                commit(
                    "SET_CONTACTS",
                    _.uniqBy([...state.contacts, res.data], (c) => c._id)
                );
                return data;
            });
        },
        setContactList({ commit, state }, data) {
            const sortedContacts = data.sort((a, b) => moment(b.lastInteractionDate) - moment(a.lastInteractionDate));
            const uniqContacts = _.uniqBy(sortedContacts, (c) => c._id);
            commit("SET_CONTACT_LIST", uniqContacts);
        },
        setContacts({ commit }, contacts) {
            commit("SET_CONTACTS", contacts);
        },
        addContact({ commit, state }, contact) {
            commit(
                "SET_CONTACTS",
                _.uniqBy([...state.contacts, contact], (c) => c._id)
            );
        },
        setContactPaginationOffset({ commit }, data) {
            commit("SET_CONTACT_PAGINATION_OFFSET", data);
        },
        setContactListSortMode({ commit }, data) {
            commit("SET_CONTACT_LIST_SORT_MODE", data);
        },
        setContactListGroupBy({ commit }, data) {
            commit("SET_CONTACT_LIST_GROUP_BY", data);
        },
        setContactListFilterMode({ commit }, data) {
            commit("SET_CONTACT_LIST_FILTER_MODE", data);
        },
        setSelectedCustomFilterTemplate({ commit }, data) {
            commit("SET_SELECTED_CUSTOM_FILTER_TEMPLATE", data);
        },

        viewContact({ rootState, state, commit }, contactId) {
            log("ChatWatcher.viewContact", contactId);
            const site = rootState.site.site;
            const authUser = rootState.auth.user;
            sendSocketMessage("user viewed-contact", {
                siteId: site._id,
                userId: authUser._id,
                contactId,
                viewed: true,
            });
        },
        joinContactConversation({ rootState, state, commit, dispatch, getters }, contact) {
            log("ChatWatcher.joinContactConversation", contact);
            const site = rootState.site.site;
            const authUser = rootState.auth.user;

            const filters = getters.getFittedContactListFilters;

            if (state.contactListFilterMode !== "custom") {
                commit("SET_CONTACT_LIST_FILTER_MODE", "custom");
            }

            sendSocketMessage("user join-conversation", {
                userId: authUser._id,
                conversationId: contact.lastConversation._id,
                siteId: site._id,
            });

            commit(
                "SET_WATCHING_CONTACT",
                state.watchingContactIds.filter((cId) => cId !== contact._id)
            );

            if (state.activeContact && contact._id !== state.activeContact._id) {
                commit("SET_PREVIOUS_CONTACT_ID", state.activeContact._id);
            }
            // commit("SET_ACTIVE_CONTACT", contact);
            dispatch("viewContact", contact._id);
            console.log("ChatWatcher SET_LAST_AGENT_STARTED_CONTACT calling from joinContactConversation");
            commit("SET_LAST_AGENT_STARTED_CONTACT", {
                contactId: contact._id,
                _id: contact._id,
                conversationId: contact.lastConversation?._id,
            });
        },
        leaveContactConversation({ rootState, state, commit, dispatch }, contact) {
            log("ChatWatcher.leaveContactConversation", contact);
            const site = rootState.site.site;
            const authUser = rootState.auth.user;

            sendSocketMessage("user leave-chat", {
                userId: authUser._id,
                conversationId: contact.lastConversation._id,
                siteId: site._id,
                watchmode: false,
            });

            commit(
                "SET_WATCHING_CONTACT",
                state.watchingContactIds.filter((cId) => cId !== contact._id)
            );
        },
        watchContactConversation({ rootState, state, commit, dispatch }, contact) {
            log("ChatWatcher.watchContactConversation", contact);
            const site = rootState.site.site;
            const authUser = rootState.auth.user;
            const isAssingToMe = contact.lastConversation.users.includes(authUser._id);
            if (isAssingToMe) return;

            sendSocketMessage("user watch-conversation", {
                userId: authUser._id,
                conversationId: contact.lastConversation._id,
                siteId: site._id,
            });

            commit("SET_WATCHING_CONTACT", [...state.watchingContactIds, contact._id]);
            console.log("ChatWatcher SET_ACTIVE_CONTACT calling from watchContactConversation");
            commit("SET_ACTIVE_CONTACT", contact);
            dispatch("viewContact", contact._id);
        },
        unWatchContactConversation({ rootState, state, commit }, contact) {
            const site = rootState.site.site;
            const authUser = rootState.auth.user;
            log("ChatWatcher.unWatchContactConversation", contact);
            commit(
                "SET_WATCHING_CONTACT",
                state.watchingContactIds.filter((cId) => cId !== contact._id)
            );
            // commit("SET_ACTIVE_CONTACT", null);

            sendSocketMessage("user unwatch-conversation", {
                userId: authUser._id,
                conversationId: contact.lastConversation?._id,
                siteId: site._id,
            });
        },
        removeContactWatchList({ state, commit }, contact) {
            log("ChatWatcher.removeContactWatchList", contact);
            commit(
                "SET_WATCHING_CONTACT",
                state.watchingContactIds.filter((cId) => cId !== contact._id)
            );
        },
        destroyContactConversation({ rootState }, contact) {
            log("ChatWatcher.destroyContactConversation", contact);
            const site = rootState.site.site;
            const authUser = rootState.auth.user;

            sendSocketMessage("user destroy-chat", {
                conversationId: contact.lastConversation._id,
                userId: authUser._id,
                siteId: site._id,
            });
        },
        updateMultipleKeyContact({ state, commit, dispatch }, { contactId, conversationId, set }) {
            log("ChatWatcher.updateMultipleKeyContact", { contactId, conversationId, set });
            dispatch(
                "setContactList",
                state.contactLists.map((c) => {
                    if (c._id === contactId || c.lastConversation._id === conversationId) {
                        c = { ...c, ...set };
                        c.lastConversation.users = _.uniq(c.lastConversation.users);
                    }
                    return c;
                })
            );
            dispatch(
                "setContacts",
                state.contacts.map((c) => {
                    if (c._id === contactId || c.lastConversation._id === conversationId) {
                        c = { ...c, ...set };
                        c.lastConversation.users = _.uniq(c.lastConversation.users);
                    }
                    return c;
                })
            );
            if (
                state.activeContact &&
                (state.activeContact?._id === contactId ||
                    state.activeContact?.lastConversation?._id === conversationId)
            ) {
                commit("SET_ACTIVE_CONTACT", {
                    ...state.activeContact,
                    ...set,
                    lastConversation: {
                        ...state.activeContact.lastConversation,
                        users: _.uniq(state.activeContact.lastConversation.users),
                    },
                });
            }
        },
        setActiveContact({ commit, state, rootState }, contact) {
            log("ChatWatcher.setActiveContact", contact);
            const site = rootState.site.site;
            const authUser = rootState.auth.user;
            if (!contact && state.activeContact) {
                sendSocketMessage("user viewed-contact", {
                    siteId: site._id,
                    userId: authUser._id,
                    contactId: state.activeContact._id,
                    viewed: false,
                });
            }
            //todo prev contact'ı sakla
            console.log("ChatWatcher SET_ACTIVE_CONTACT calling from setActiveContact");
            commit("SET_ACTIVE_CONTACT", contact);
            const isExists = state.contactLists.find((c) => c._id === contact?._id);
            if (isExists) {
                console.log("ChatWatcher SET_LAST_AGENT_STARTED_CONTACT calling from setActiveContact");
                commit("SET_LAST_AGENT_STARTED_CONTACT", null);
            }
        },
        incContactUnreadCount({ state, commit, dispatch }, { contactId }) {
            log("ChatWatcher.incContactUnreadCount", contactId);
            if (contactId === state.activeContact?._id) {
                console.log("this message is active contact message");
                return;
            }
            dispatch(
                "setContactList",
                state.contactLists.map((c) => {
                    if (c._id === contactId) {
                        c = {
                            ...c,
                            unreadedCount: c.unreadedCount + 1,
                        };
                    }
                    return c;
                })
            );
            dispatch(
                "setContacts",
                state.contacts.map((c) => {
                    if (c._id === contactId) {
                        c = {
                            ...c,
                            unreadedCount: c.unreadedCount + 1,
                        };
                    }
                    return c;
                })
            );
            if (state.activeContact?._id === contactId) {
                commit("SET_ACTIVE_CONTACT", {
                    ...state.activeContact,
                    unreadedCount: state.activeContact.unreadedCount + 1,
                });
            }
        },
        updateContactFilters({ commit }, filters) {
            log("ChatWatcher.updateContactFilters timeSelect: " + filters.timeSelect);

            if (!!filters.timeSelect && filters.timeSelect !== "custom") {
                filters.startDate = undefined;
                filters.endDate = undefined;
            }
            commit("SET_CONTACT_LIST_FILTERS", filters);
        },
        startChat({ rootState, state, commit }, contact) {
            log("ChatWatcher.startChat", contact);
            let payload = {
                siteId: rootState.site.site._id,
                userId: rootState.auth.user._id,
                visitorId: contact._id,
                channelId: contact.channel,
            };
            SYSTEM_PUBSUB.publish(SYSTEM_PUBSUB.MESSAGE_TYPES.APPLY_FILTER_TEMPLATE, "all-agent-inbox");
            commit(
                "SET_WATCHING_CONTACT",
                state.watchingContactIds.filter((cId) => cId !== contact._id)
            );

            if (contact.platform === "whatsapp") {
                payload.whatsappNumber = contact.whatsappNumber;
            } else if (contact.platform === "instagram") {
                payload.instagramUserId = contact.instagramUserId;
            } else if (contact.platform === "messenger") {
                payload.messengerUserId = contact.messengerUserId;
            }

            sendSocketMessage("user start-chat", payload);
        },
        setMultiChatViewerMode({ commit }, data) {
            log("ChatWatcher.setMultiChatViewerMode", data);
            commit("SET_MULTI_CHAT_VIEWER_MODE", data);
        },
        setLastAgentStartedContact({ commit }, id) {
            log("ChatWatcher.setLastAgentStartedContact", id);
            commit("SET_LAST_AGENT_STARTED_CONTACT", id);
        },
        setChatListToolbarLocked({ commit }, locked) {
            commit("SET_CHAT_LIST_TOOLBAR_LOCKED", locked);
        },
    },
};

export default chat;
